import { BUILD_ID } from '../version';

export const WEBSITE_VERSION_KEY = 'xiaoying:website-version';
export const WEBSITE_RELOAD_PARAM = '_xy_version';
export const WEBSITE_RELOAD_ATTEMPT_KEY = 'xiaoying:website-reload-attempt';
export const WEBSITE_NAVIGATION_EVENT = 'xiaoying:website-navigation';

export function hasAttemptedWebsiteReload(buildId) {
  try { return sessionStorage.getItem(WEBSITE_RELOAD_ATTEMPT_KEY) === buildId; } catch { return false; }
}

export function reloadWebsiteVersion(buildId, { automatic = false, reload = url => window.location.replace(url) } = {}) {
  if (automatic && hasAttemptedWebsiteReload(buildId)) return false;
  try { sessionStorage.setItem(WEBSITE_RELOAD_ATTEMPT_KEY, buildId); } catch { /* The URL marker also prevents immediate loops. */ }
  reload(websiteReloadTarget(window.location.href, buildId));
  return true;
}

export function isWebsiteUpdateBrowsingRoute(pathname, search = '') {
  const route = String(pathname || '').replace(/^\/prototype\/vnext(?=\/|$)/, '').replace(/\/$/, '') || '/';
  // Editors, account forms, payment flows, video playback and unknown routes are never auto-refreshed.
  return ['/home', '/writing', '/canvas', '/assets', '/templates'].includes(route)
    && !new URLSearchParams(search).has('tab') && !new URLSearchParams(search).has('send');
}

export function hasWebsiteUpdateInteraction() {
  if (document.querySelector('[role="dialog"], [aria-modal="true"], [data-account-unsaved="true"], [data-account-busy="true"], [aria-busy="true"], [contenteditable="true"]')) return true;
  if (document.activeElement?.matches('select')) return true;
  return Array.from(document.querySelectorAll('input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea'))
    .some(field => !field.closest('.website-version') && (field === document.activeElement || String(field.value || '').trim()));
}

export function rememberWebsiteVersion(buildId) {
  try { localStorage.setItem(WEBSITE_VERSION_KEY, buildId); } catch { /* Storage may be unavailable. */ }
}

export function hasWebsiteUpdate(buildId) {
  try {
    const previous = localStorage.getItem(WEBSITE_VERSION_KEY);
    // Establish a baseline for first-time visitors; only returning visitors see an update.
    if (!previous) { rememberWebsiteVersion(buildId); return false; }
    return previous !== buildId;
  } catch { return false; }
}

export function websiteReloadTarget(target, marker) {
  const url = new URL(target, window.location.origin);
  url.searchParams.set(WEBSITE_RELOAD_PARAM, marker);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function clearWebsiteReloadMarker() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(WEBSITE_RELOAD_PARAM)) return;
  url.searchParams.delete(WEBSITE_RELOAD_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

// Discovery is read-only; the caller separately decides when refreshing is safe.
export async function fetchWebsiteVersion({ signal } = {}) {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store', signal });
    if (!response.ok) return null;
    const latest = await response.json();
    if (signal?.aborted) return null;
    if (typeof latest?.buildId !== 'string' || !/^[a-zA-Z0-9._-]{1,128}$/.test(latest.buildId)
        || typeof latest.version !== 'string' || !Number.isFinite(Date.parse(latest.builtAt))) return null;
    return latest;
  } catch { return null; }
}

// Entry-time refresh happens before the workspace is mounted, while there are no edits to lose.
export async function checkWebsiteVersion({ signal, buildId = BUILD_ID, reload = url => window.location.replace(url) } = {}) {
  if (!buildId) return 'development';
  const latest = await fetchWebsiteVersion({ signal });
  if (!latest) return 'unavailable';
  if (latest.buildId === buildId) return 'current';
  // One automatic attempt per target build/tab, including later background discovery.
  if (new URL(window.location.href).searchParams.has(WEBSITE_RELOAD_PARAM)) return 'outdated';
  return reloadWebsiteVersion(latest.buildId, { automatic: true, reload }) ? 'reloading' : 'outdated';
}

export function reloadWebsiteAfterLogin(target, buildId = BUILD_ID, reload = url => window.location.replace(url)) {
  if (!buildId) return false;
  // A fresh document also refreshes code when the login page was open during a deployment.
  reload(websiteReloadTarget(target, `login-${Date.now()}`));
  return true;
}
